import { useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { DicomStructureSet } from '~/domain/dicom/dicomstructureset';
import { Roi } from '~/domain/patient/roi';
import { useRoiCommands } from '~/hooks/domain/useRoiCommands';
import {
    replanningReconciledStructuresState,
    replanningRunOutputsState,
} from '~/state/atoms/adaptiveReplanningAtoms';
import { useReviewDatasets } from './useReviewDatasets';

const namesEqual = DicomStructureSet.structureNamesTrimmedEqual;

const sameColor = (a: DicomColor, b: DicomColor) =>
    a.R === b.R && a.G === b.G && a.B === b.B;

// Once per run, before any edit: each propagated structure takes its source's name and color, renamed pairs resolved by the run's pairing.
export function useReconcilePropagatedStructures(): void {
    const { sourceSet, propagatedSet } = useReviewDatasets();
    const { contourRois } = useRecoilValue(replanningRunOutputsState);
    const { rename, setColor } = useRoiCommands();
    const [reconciled, setReconciled] = useRecoilState(
        replanningReconciledStructuresState
    );

    useEffect(() => {
        if (
            !sourceSet ||
            !propagatedSet ||
            propagatedSet.isApproved ||
            reconciled === propagatedSet.dicomUid
        ) {
            return;
        }
        setReconciled(propagatedSet.dicomUid);
        const sourceNamed = (name: string): Roi | undefined =>
            sourceSet.rois.find((candidate) =>
                namesEqual(candidate.name, name)
            );
        for (const roi of propagatedSet.rois) {
            // Its own name if it kept the source's, else the source name the run paired this model structure with.
            const exact = sourceNamed(roi.name);
            const paired = exact
                ? undefined
                : (contourRois ?? []).find((assignment) =>
                      namesEqual(assignment.modelRoiName, roi.name)
                  );
            const source =
                exact ?? (paired ? sourceNamed(paired.name) : undefined);
            if (!source) {
                continue;
            }
            if (!namesEqual(roi.name, source.name)) {
                rename(roi.id, source.name);
            }
            if (!sameColor(roi.color, source.color)) {
                setColor(roi.id, { ...source.color });
            }
        }
    }, [
        sourceSet,
        propagatedSet,
        contourRois,
        reconciled,
        setReconciled,
        rename,
        setColor,
    ]);
}
